import { Trans } from '@lingui/macro';
import * as Sentry from '@sentry/react';
// BrowserTracing is now included in @sentry/react
import { log } from './logger';

// Performance monitoring metrics
export class PerformanceMonitor {
  private static instance: PerformanceMonitor;
  private metrics: Map<string, number> = new Map();
  private observers: Map<string, PerformanceObserver> = new Map();

  private constructor() {
    this.initializeObservers();
  }

  public static getInstance(): PerformanceMonitor {
    if (!PerformanceMonitor.instance) {
      PerformanceMonitor.instance = new PerformanceMonitor();
    }
    return PerformanceMonitor.instance;
  }

  private initializeObservers() {
    // Observe navigation timing
    if ('PerformanceObserver' in window) {
      try {
        // Core Web Vitals
        const observer = new PerformanceObserver((list) => {
          list.getEntries().forEach((entry) => {
            if (entry.entryType === 'navigation') {
              const navEntry = entry as PerformanceNavigationTiming;
              this.trackMetric('page_load_time', navEntry.loadEventEnd - navEntry.startTime);
              this.trackMetric('dom_content_loaded', navEntry.domContentLoadedEventEnd - navEntry.startTime);
              this.trackMetric('first_paint', navEntry.loadEventStart - navEntry.startTime);
            }

            if (entry.entryType === 'largest-contentful-paint') {
              this.trackMetric('largest_contentful_paint', entry.startTime);
            }

            if (entry.entryType === 'first-input') {
              const fidEntry = entry as PerformanceEventTiming;
              this.trackMetric('first_input_delay', fidEntry.processingStart - fidEntry.startTime);
            }

            if (entry.entryType === 'layout-shift') {
              const clsEntry = entry as any;
              if (!clsEntry.hadRecentInput) {
                this.trackMetric('cumulative_layout_shift', clsEntry.value);
              }
            }
          });
        });

        observer.observe({ entryTypes: ['navigation', 'largest-contentful-paint', 'first-input', 'layout-shift'] });
        this.observers.set('core-web-vitals', observer);
      } catch (error) {
        log.warn('Failed to initialize performance observers', error);
      }
    }
  }

  public trackMetric(name: string, value: number) {
    this.metrics.set(name, value);
    log.performanceMetric(name, value);
    
    // Send to Sentry as measurement
    Sentry.setMeasurement(name, value, 'millisecond');
  }

  public startTimer(name: string): () => void {
    const startTime = performance.now();
    return () => {
      const duration = performance.now() - startTime;
      this.trackMetric(name, duration);
    };
  }

  public getMetrics(): Record<string, number> {
    return Object.fromEntries(this.metrics);
  }

  public trackApiCall<T>(
    promise: Promise<T>,
    method: string,
    url: string
  ): Promise<T> {
    const startTime = performance.now();
    const endTimer = this.startTimer(`api_call_${method.toLowerCase()}_${url.replace(/[^a-z0-9]/gi, '_')}`);

    return promise
      .then((result) => {
        const duration = performance.now() - startTime;
        log.apiCall(method, url, 200, duration);
        endTimer();
        return result;
      })
      .catch((error) => {
        const duration = performance.now() - startTime;
        const status = error?.response?.status || 500;
        log.apiCall(method, url, status, duration, error);
        endTimer();
        throw error;
      });
  }

  public destroy() {
    this.observers.forEach((observer) => observer.disconnect());
    this.observers.clear();
    this.metrics.clear();
  }
}

// Error boundary integration
export interface ErrorBoundaryInfo {
  componentStack: string;
  errorBoundary?: string;
}

export const initializeMonitoring = () => {
  const glitchtipDsn = import.meta.env.VITE_GLITCHTIP_DSN;
  const environment = import.meta.env.VITE_NODE_ENV || import.meta.env.MODE;
  const release = import.meta.env.VITE_APP_VERSION || '1.0.0';

  if (!glitchtipDsn) {
    log.warn('GlitchTip DSN not configured - error tracking disabled');
    return;
  }

  // Initialize Sentry/GlitchTip
  Sentry.init({
    dsn: glitchtipDsn,
    environment,
    release,
    
    // Performance monitoring
    integrations: [
      Sentry.browserTracingIntegration({
        // Set up automatic route change tracking for SPA
      }),
    ],
    
    // Performance monitoring sample rate (0.1 = 10%)
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
    
    // Error sampling rate
    sampleRate: environment === 'production' ? 1.0 : 1.0,
    
    // Additional configuration
    beforeSend(event, hint) {
      // Filter out development errors in production
      if (environment === 'production') {
        const error = hint.originalException;
        
        // Don't send CORS errors
        if (error && error.toString().includes('CORS')) {
          return null;
        }
        
        // Don't send cancelled requests
        if (error && error.toString().includes('canceled')) {
          return null;
        }
        
        // Don't send network errors from browser extensions
        if (error && error.toString().includes('Extension')) {
          return null;
        }
      }
      
      return event;
    },
    
    // Breadcrumbs configuration
    maxBreadcrumbs: 100,
    
    // Additional context
    initialScope: {
      tags: {
        component: 'frontend',
        platform: 'web'
      },
      contexts: {
        app: {
          name: import.meta.env.VITE_APP_NAME || 'Jema Manager',
          version: release
        },
        browser: {
          name: navigator.userAgent,
          language: navigator.language,
          cookieEnabled: navigator.cookieEnabled
        }
      }
    }
  });

  // Initialize performance monitoring
  PerformanceMonitor.getInstance();

  log.info('Monitoring initialized', {
    environment,
    release,
    hasGlitchTip: !!glitchtipDsn
  });
};

// React Error Boundary component
export const ErrorBoundary = Sentry.withErrorBoundary(
  ({ children }: { children: React.ReactNode }) => <>{children}</>,
  {
    fallback: ({ error, resetError }) => (
      <div className="min-h-screen flex items-center justify-center bg-background text-foreground px-4">
        <div className="max-w-md w-full">
          <div className="bg-card text-card-foreground shadow-lg rounded-lg p-6">
            <div className="flex items-center justify-center w-12 h-12 mx-auto rounded-full bg-destructive/10">
              <svg className="w-6 h-6 text-destructive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div className="mt-4 text-center">
              <h3 className="text-lg font-medium text-foreground"><Trans id='shared.lib.monitoring.something-went-wrong'>Something went wrong</Trans></h3>
              <p className="mt-2 text-sm text-muted-foreground"><Trans id='shared.lib.monitoring.an-unexpected-error-occurred-our'>An unexpected error occurred. Our team has been notified.</Trans></p>
              <div className="mt-4 space-y-2">
                <button
                  onClick={resetError}
                  className="w-full inline-flex justify-center px-4 py-2 text-sm font-medium text-destructive-foreground bg-destructive border border-transparent rounded-md shadow-sm hover:bg-destructive/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
                ><Trans id='shared.lib.monitoring.try-again'>Try Again</Trans></button>
                <button
                  onClick={() => window.location.href = '/'}
                  className="w-full inline-flex justify-center px-4 py-2 text-sm font-medium text-foreground bg-background border border-border rounded-md shadow-sm hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
                ><Trans id='shared.lib.monitoring.go-home'>Go Home</Trans></button>
              </div>
              {import.meta.env.VITE_ENABLE_DEBUG === 'true' && (
                <details className="mt-4 text-left">
                  <summary className="cursor-pointer text-sm text-muted-foreground"><Trans id='shared.lib.monitoring.error-details-debug-mode'>Error Details (Debug Mode)</Trans></summary>
                  <pre className="mt-2 text-xs text-destructive bg-destructive/10 p-2 rounded overflow-auto">
                    {error?.toString()}
                  </pre>
                </details>
              )}
            </div>
          </div>
        </div>
      </div>
    ),
    beforeCapture: (scope, error, errorInfo) => {
      scope.setTag('errorBoundary', true);
      scope.setContext('errorInfo', errorInfo as any);
      log.error('React Error Boundary triggered', error, errorInfo);
    }
  }
);

// Export performance monitor instance
export const performanceMonitor = PerformanceMonitor.getInstance();

// Utility functions for manual error reporting
export const reportError = (error: Error, context?: Record<string, any>) => {
  log.error('Manual error report', error, context);
  Sentry.withScope((scope) => {
    if (context) {
      scope.setContext('manual_report', context);
    }
    Sentry.captureException(error);
  });
};

export const reportMessage = (
  message: string, 
  level: 'info' | 'warning' | 'error' = 'info',
  context?: Record<string, any>
) => {
  const logLevel = level === 'warning' ? 'warn' : level;
  (log as any)[logLevel](message, context);
  Sentry.withScope((scope) => {
    if (context) {
      scope.setContext('manual_message', context);
    }
    Sentry.captureMessage(message, level);
  });
};
